import { queryClient } from "@/config/react-query";

import type { Page } from "@/types/pagination";
import type {
  MergeSummary,
  PatientChart,
  PatientSummary,
  PatientTableRequest,
} from "@/types/patient";
import type { PatientSidebarCounts } from "@/types/patient-sidebar";
import type { PatientTag } from "@/types/patient-tag";
import type { ReviewSummary } from "@/types/review";

import type { PatientEditLogEntry } from "./patient";
import {
  checkIfExternalIdIsUnique,
  fetchPatientIds,
  fetchPatientTable,
  findDuplicates,
  findDuplicatesOfPatient,
  getBlacklist,
  getLeadSources,
  getPatient,
  getPatientEditHistory,
  getPatientReviews,
  listPatients,
  searchPatient,
} from "./patient";
import { getPatientTags } from "./patient-tag";
import { getPatientSidebarCounts } from "./sidebar";

export const patientQueryKeys = {
  all: ["user", "patient"] as const,
  detail: (patientId: string) =>
    [...patientQueryKeys.all, "detail", patientId] as const,
  reviews: (patientId: string) =>
    [...patientQueryKeys.all, "reviews", patientId] as const,
  editHistory: (patientId: string) =>
    [...patientQueryKeys.all, "edit-history", patientId] as const,
  sidebarCountsForPatient: (patientId: string) =>
    [...patientQueryKeys.all, "sidebar-snapshot", patientId] as const,
  sidebarCounts: (patientId: string, includeCancelled: boolean | null = null) =>
    [
      ...patientQueryKeys.sidebarCountsForPatient(patientId),
      includeCancelled ?? "",
    ] as const,
  searches: () => [...patientQueryKeys.all, "search"] as const,
  search: (term: string, n?: number) =>
    [...patientQueryKeys.searches(), term, n ?? ""] as const,
  blacklist: () => [...patientQueryKeys.all, "blacklist"] as const,
  table: (params: PatientTableRequest) =>
    [...patientQueryKeys.all, "table", params] as const,
  duplicates: (fields: string[]) =>
    [...patientQueryKeys.all, "duplicates", ...fields] as const,
  duplicatesOfPatient: (patientId: string, fields?: string[]) =>
    [
      ...patientQueryKeys.all,
      "duplicates-of-patient",
      patientId,
      ...(fields || []),
    ] as const,
  ids: (params: PatientTableRequest) =>
    [...patientQueryKeys.all, "ids", params] as const,
  list: (patientIds: string[]) =>
    [...patientQueryKeys.all, "list", ...patientIds] as const,
  externalIdUnique: (externalId: string) =>
    [...patientQueryKeys.all, "external-id-unique", externalId] as const,
  leadSources: () => [...patientQueryKeys.all, "lead-sources"] as const,
  patientTags: () => [...patientQueryKeys.all, "patient-tags"] as const,
  patientTagsStrict: () =>
    [...patientQueryKeys.all, "patient-tags", "strict"] as const,
};

export const patientQueryOptions = {
  detail: (patientId: string) => ({
    queryKey: patientQueryKeys.detail(patientId),
    queryFn: () => getPatient(patientId),
    select: (data: PatientChart | null) => data ?? undefined,
  }),
  reviews: (patientId: string) => ({
    queryKey: patientQueryKeys.reviews(patientId),
    queryFn: () => getPatientReviews(patientId),
    select: (data: ReviewSummary[] | null) => data ?? [],
  }),
  editHistory: (patientId: string) => ({
    queryKey: patientQueryKeys.editHistory(patientId),
    queryFn: () => getPatientEditHistory(patientId),
    select: (data: PatientEditLogEntry[] | null) => data ?? [],
  }),
  search: (
    term: string,
    controllerOrSignal?: AbortController | AbortSignal | null,
    n?: number,
  ) => ({
    queryKey: patientQueryKeys.search(term, n),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      searchPatient(term, controllerOrSignal ?? signal, n),
    select: (data: PatientSummary[] | null) => data ?? [],
  }),
  blacklist: () => ({
    queryKey: patientQueryKeys.blacklist(),
    queryFn: () => getBlacklist(),
    select: (data: PatientSummary[] | null) => data ?? [],
  }),
  table: (
    params: PatientTableRequest,
    controller?: AbortController | null,
  ) => ({
    queryKey: patientQueryKeys.table(params),
    queryFn: () => fetchPatientTable(params, controller ?? undefined),
  }),
  duplicates: (fields: string[]) => ({
    queryKey: patientQueryKeys.duplicates(fields),
    queryFn: () => findDuplicates(fields),
    select: (data: MergeSummary[] | null) => data ?? [],
  }),
  duplicatesOfPatient: (patientId: string, fields?: string[]) => ({
    queryKey: patientQueryKeys.duplicatesOfPatient(patientId, fields),
    queryFn: () => findDuplicatesOfPatient(patientId, fields),
    select: (data: PatientSummary[] | null) => data ?? [],
  }),
  ids: (params: PatientTableRequest, controller?: AbortController | null) => ({
    queryKey: patientQueryKeys.ids(params),
    queryFn: () => fetchPatientIds(params, controller ?? undefined),
    select: (data: string[] | null) => data ?? [],
  }),
  list: (patientIds: string[]) => ({
    queryKey: patientQueryKeys.list(patientIds),
    queryFn: () => listPatients(patientIds),
    select: (data: PatientSummary[] | null) => data ?? [],
  }),
  externalIdUnique: (externalId: string) => ({
    queryKey: patientQueryKeys.externalIdUnique(externalId),
    queryFn: () => checkIfExternalIdIsUnique(externalId),
  }),
  leadSources: () => ({
    queryKey: patientQueryKeys.leadSources(),
    queryFn: () => getLeadSources(),
    select: (data: string[] | null) => data ?? [],
  }),
  patientTags: () => ({
    queryKey: patientQueryKeys.patientTags(),
    queryFn: () => getPatientTags(),
    select: (data: Page<PatientTag> | null) => data?.data ?? [],
  }),
  // Same catalog, but a failed request rejects rather than resolving null.
  // Callers that decide something is gone because it is absent from the list
  // must use this one: with the swallowing variant a 403 or a dropped
  // connection is indistinguishable from a tenant that has no tags.
  //
  // Includes archived tags. A caller here is reconciling against tags it
  // already references, so it has to tell "archived" apart from "deleted" --
  // the default list drops archived rows and would conflate the two.
  patientTagsStrict: () => ({
    queryKey: patientQueryKeys.patientTagsStrict(),
    queryFn: async () => (await getPatientTags(false, true))?.data ?? [],
  }),
  sidebarCounts: (
    patientId: string,
    includeCancelled: boolean | null = null,
  ) => ({
    queryKey: patientQueryKeys.sidebarCounts(patientId, includeCancelled),
    queryFn: () => getPatientSidebarCounts(patientId, includeCancelled),
    select: (data: PatientSidebarCounts | null) => data ?? undefined,
    initialData: queryClient.getQueryData<PatientSidebarCounts | undefined>(
      patientQueryKeys.sidebarCounts(patientId, !includeCancelled),
    ),
  }),
};
